import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useDarkMode, type UseDarkModeResult } from '../hooks/useDarkMode';

const ThemeContext = createContext<UseDarkModeResult>({
    isDark: false,
    mode: 'light',
    isDarkModeFeatureEnabled: true,
    setMode: () => undefined,
    toggle: () => undefined,
    setIsDark: () => undefined,
    setDarkModeFeatureEnabled: () => undefined,
});

export interface ThemeProviderProps {
    children: ReactNode;
}

/** Provides app-wide theme state (light / dark). Wrap the app once near the root. */
export function ThemeProvider({ children }: Readonly<ThemeProviderProps>) {
    const {
        isDark,
        mode,
        isDarkModeFeatureEnabled,
        setMode,
        toggle,
        setIsDark,
        setDarkModeFeatureEnabled,
    } = useDarkMode();

    const value = useMemo(
        () => ({
            isDark,
            mode,
            isDarkModeFeatureEnabled,
            setMode,
            toggle,
            setIsDark,
            setDarkModeFeatureEnabled,
        }),
        [isDark, mode, isDarkModeFeatureEnabled, setMode, toggle, setIsDark, setDarkModeFeatureEnabled]
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Read the current theme state. */
export function useTheme(): UseDarkModeResult {
    return useContext(ThemeContext);
}
